import React, { useState, useEffect } from 'react';
import { addDays, format, parseISO, differenceInCalendarDays } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { changeMealPlanDates } from '@/lib/api/mealPlans';
import { extractErrorMessage } from '@/utils/toast-utils';
import {
  extractMealPlanDateConflicts,
  type MealPlanDateConflictItem,
} from '@/utils/meal-plan-date-conflicts';
import InlineFeedback from '@/components/shared/InlineFeedback';
import { Loader2, Calendar } from 'lucide-react';

const SHORTEN_CONFIRM_MESSAGE =
  'Reducing the duration will permanently delete the last day(s) of the meal plan. Do you want to continue?';

function parseDate(s: string): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  try {
    return parseISO(s);
  } catch {
    return null;
  }
}

function daysInclusive(start: string, end: string): number {
  const s = parseDate(start);
  const e = parseDate(end);
  if (!s || !e || e < s) return 0;
  return differenceInCalendarDays(e, s) + 1;
}

export interface ChangeMealPlanDatesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mealPlanId: string;
  currentStartDate: string;
  currentEndDate: string;
  currentDurationDays: number;
  onSuccess: () => void;
}

export function ChangeMealPlanDatesModal({
  open,
  onOpenChange,
  mealPlanId,
  currentStartDate,
  currentEndDate,
  currentDurationDays,
  onSuccess,
}: ChangeMealPlanDatesModalProps) {
  const [newStartDate, setNewStartDate] = useState(currentStartDate);
  const [newEndDate, setNewEndDate] = useState(currentEndDate);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShortenConfirm, setShowShortenConfirm] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<{ newStartDate: string; newEndDate: string } | null>(null);
  const [dateConflicts, setDateConflicts] = useState<MealPlanDateConflictItem[]>([]);
  const [dateFeedback, setDateFeedback] = useState<{
    variant: 'error' | 'warning' | 'success' | 'info';
    title?: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setNewStartDate(currentStartDate);
      setNewEndDate(currentEndDate);
      setPendingPayload(null);
      setDateConflicts([]);
      setDateFeedback(null);
    }
  }, [open, currentStartDate, currentEndDate]);

  const newDurationDays = daysInclusive(newStartDate, newEndDate);
  const isShortening = newDurationDays >= 1 && newDurationDays < currentDurationDays;
  const isRangeValid = newDurationDays >= 1 && newDurationDays <= 7;
  const isStartValid = parseDate(newStartDate) != null;
  const isEndValid = parseDate(newEndDate) != null && parseDate(newEndDate)! >= parseDate(newStartDate)!;
  const maxEndDate = (() => {
    const parsedStart = parseDate(newStartDate);
    if (!parsedStart) return undefined;
    return format(addDays(parsedStart, 6), 'yyyy-MM-dd');
  })();

  useEffect(() => {
    const parsedStart = parseDate(newStartDate);
    const parsedEnd = parseDate(newEndDate);
    if (!parsedStart || !parsedEnd) return;

    const maxEnd = addDays(parsedStart, 6);
    if (parsedEnd < parsedStart) {
      setNewEndDate(newStartDate);
      return;
    }
    if (parsedEnd > maxEnd) {
      setNewEndDate(format(maxEnd, 'yyyy-MM-dd'));
    }
  }, [newStartDate, newEndDate]);

  const handleSubmit = () => {
    if (!mealPlanId || !newStartDate || !newEndDate) return;
    if (newDurationDays < 1 || newDurationDays > 7) return;
    if (parseDate(newEndDate)! < parseDate(newStartDate)!) return;

    const payload = { newStartDate, newEndDate };
    if (isShortening) {
      setPendingPayload(payload);
      setShowShortenConfirm(true);
      return;
    }
    submitPayload(payload);
  };

  const submitPayload = async (payload: { newStartDate: string; newEndDate: string }, overwriteConflicts = false) => {
    setIsSubmitting(true);
    let keepPendingForConflict = false;
    try {
      const res = await changeMealPlanDates(mealPlanId, { ...payload, overwriteConflicts });
      if (res?.success && res?.data) {
        setDateFeedback(null);
        onSuccess();
        onOpenChange(false);
      } else {
        setDateFeedback({
          variant: 'error',
          title: 'Failed to update dates',
          message: res?.message ?? 'Failed to update dates.',
        });
      }
    } catch (err) {
      const conflicts = extractMealPlanDateConflicts(err);
      if (conflicts.length > 0) {
        keepPendingForConflict = true;
        setDateConflicts(conflicts);
        setPendingPayload(payload);
        setDateFeedback(null);
        return;
      }
      setDateFeedback({
        variant: 'error',
        title: 'Failed to update dates',
        message: extractErrorMessage(err),
      });
    } finally {
      setIsSubmitting(false);
      setShowShortenConfirm(false);
      if (!keepPendingForConflict) {
        setPendingPayload(null);
        setDateConflicts([]);
      }
    }
  };

  const handleConfirmShorten = () => {
    if (pendingPayload) submitPayload(pendingPayload);
  };

  const handleConfirmOverwrite = () => {
    if (pendingPayload) submitPayload(pendingPayload, true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Change dates
            </DialogTitle>
            <DialogDescription>
              Choose start and end dates for this manual meal plan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Current: {format(parseDate(currentStartDate) ?? new Date(), 'MMM d, yyyy')} – {format(parseDate(currentEndDate) ?? new Date(), 'MMM d, yyyy')} ({currentDurationDays} day{currentDurationDays !== 1 ? 's' : ''})
            </p>
            <div className="grid gap-2">
              <Label htmlFor="newStartDate">Start date</Label>
              <Input
                id="newStartDate"
                type="date"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="newEndDate">End date (1–7 days from start)</Label>
              <Input
                id="newEndDate"
                type="date"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                min={newStartDate}
                max={maxEndDate}
                disabled={isSubmitting}
              />
              {newDurationDays > 0 && (
                <p className="text-xs text-muted-foreground">
                  {newDurationDays} day{newDurationDays !== 1 ? 's' : ''}
                  {newDurationDays > 7 && ' (max 7 days)'}
                  {isShortening && ' — last day(s) will be permanently deleted'}
                </p>
              )}
            </div>
            {dateFeedback ? (
              <InlineFeedback
                variant={dateFeedback.variant}
                title={dateFeedback.title}
                message={dateFeedback.message}
              />
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !isStartValid || !isEndValid || !isRangeValid}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating…
                </>
              ) : (
                'Update dates'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showShortenConfirm} onOpenChange={setShowShortenConfirm}>
        <AlertDialogContent>
          <AlertDialogTitle>Reduce duration?</AlertDialogTitle>
          <AlertDialogDescription>{SHORTEN_CONFIRM_MESSAGE}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmShorten}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating…
                </>
              ) : (
                'Confirm'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={dateConflicts.length > 0} onOpenChange={(open) => !open && setDateConflicts([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite existing meal plans?</AlertDialogTitle>
            <AlertDialogDescription>
              These active plans will be marked inactive if you continue:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {dateConflicts.map((item) => (
              <li key={item.id}>
                {item.name} ({item.startDate || 'Unknown'} - {item.endDate || 'Unknown'})
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmOverwrite}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting ? 'Updating...' : 'Overwrite dates'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
